/**
 * SingleRowForm - Form for single row box hangar template
 *
 * Supports 1-20 units with auto-calculated or manually adjusted widths.
 * Updates preview in real-time as values change.
 */

import { useForm, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo, useState, useRef } from 'react'
import { ChevronDown, ChevronRight, Trash2, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import {
  singleRowParamsSchema,
  DEFAULT_SINGLE_ROW_VALUES,
  type SingleRowParams,
  getUnitDoors as getUnitDoorsFromParams,
} from '@/lib/templates/single-row/params'
import { calculateSingleRowLayout, type SuggestedFix } from '@/lib/templates/single-row/calculations'
import { DOOR_TYPE_DEFAULTS, type DoorType } from '@/lib/templates/types'
import { UnitLayoutPreview } from '../shared/UnitLayoutPreview'
import { ConstraintWarning } from '../shared/ConstraintWarning'
import { SingleRowUnitDoorEditor } from './SingleRowUnitDoorEditor'
import { useBuildingEditorStore } from '@/stores/buildingEditorStore'
import { RemoveAreaButton } from '../shared/RemoveAreaButton'
import { Form } from '@/components/ui/form'
import { PricingFeaturesSection } from './PricingFeaturesSection'
import { pricingFieldsSchema, defaultPricingValues, type PricingFormFields } from '@/lib/schemas/pricing-fields'
import type { UnitDoorsConfig } from '@/lib/templates/types'
import { UNIT_TYPE_LABELS, type UnitType } from '@/types/units'
import { ALLOWED_UNIT_TYPES_FOR_AREA } from '@/types/type-defaults'

interface SingleRowFormProps {
  initialValues?: Partial<SingleRowParams>
  onValuesChange?: (values: SingleRowParams) => void
  onSave: (values: SingleRowParams, pricing?: PricingFormFields) => void
  onCancel: () => void
  isCreating?: boolean
  isSaving?: boolean
  previewLatitude?: number
  previewLongitude?: number
  isPlaced?: boolean  // When false, building follows cursor - disable save until placed
  /** Area ID for delete functionality (edit mode only) */
  areaId?: string
  /** Area name for delete confirmation message */
  areaName?: string
  /** Called after area is successfully removed */
  onRemoved?: () => void
  /** Initial pricing values (for edit mode) */
  initialPricing?: Partial<PricingFormFields>
}

export function SingleRowForm({
  initialValues,
  onValuesChange,
  onSave,
  onCancel,
  isCreating = true,
  isSaving = false,
  previewLatitude,
  previewLongitude,
  isPlaced = true,
  areaId,
  areaName,
  onRemoved,
  initialPricing,
}: SingleRowFormProps) {
  const [openUnits, setOpenUnits] = useState<Set<number>>(new Set())
  const isInitialMount = useRef(true)

  // Get store values and actions
  const {
    singleRowFormValues,
        updateSingleRowFormValues,
      } = useBuildingEditorStore()

  const form = useForm<SingleRowParams>({
    // zodResolver types its result as Resolver<z.input, any, z.output>; schema
    // `.default()`s make those differ, so it will not unify with the form's
    // output-typed values under react-hook-form v7.67. Defaults are applied at
    // parse time, so the cast is sound.
    resolver: zodResolver(singleRowParamsSchema) as unknown as Resolver<SingleRowParams>,
    defaultValues: {
      ...DEFAULT_SINGLE_ROW_VALUES,
      ...initialValues,
      // Use store values if available (for resuming edit)
      ...(singleRowFormValues || {}),
    },
  })

  const pricingForm = useForm<PricingFormFields>({
    // zodResolver types its result as Resolver<z.input, any, z.output>; schema
    // `.default()`s make those differ, so it will not unify with the form's
    // output-typed values under react-hook-form v7.67. Defaults are applied at
    // parse time, so the cast is sound.
    resolver: zodResolver(pricingFieldsSchema) as unknown as Resolver<PricingFormFields>,
    defaultValues: { ...defaultPricingValues, ...initialPricing },
  })

  const { register, handleSubmit, watch, setValue,  formState: { errors } } = form

  // Watch all values
  const watchedValues = watch()

  // Calculate layout with current values
  const layoutResult = useMemo(() => {
    return calculateSingleRowLayout(watchedValues)
  }, [
    watchedValues.width_ft,
    watchedValues.unit_count,
    watchedValues.wall_thickness_ft,
    watchedValues.unit_overrides,
  ])

  // Sync form values to store for preview updates
  useEffect(() => {
    // Skip initial mount to avoid overwriting store values
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Update store with current form values (triggers preview update)
    updateSingleRowFormValues(watchedValues)
  }, [
    watchedValues.width_ft,
    watchedValues.depth_ft,
    watchedValues.height_ft,
    watchedValues.unit_count,
    watchedValues.wall_thickness_ft,
    watchedValues.default_door_type,
    watchedValues.default_door_width_ft,
    watchedValues.default_door_height_ft,
    watchedValues.floor_color,
    watchedValues.wall_color,
    watchedValues.bearing_deg,
    watchedValues.name,
    watchedValues.code,
    JSON.stringify(watchedValues.unit_overrides),
    updateSingleRowFormValues,
  ])

  // Sync bearing from store to form when changed externally (e.g., rotation ring)
  useEffect(() => {
    const storeBearing = singleRowFormValues?.bearing_deg
    const formBearing = watchedValues.bearing_deg

    // Only sync if store value differs (external change)
    if (storeBearing !== undefined && Math.abs(storeBearing - formBearing) > 0.1) {
      setValue('bearing_deg', storeBearing)
    }
  }, [singleRowFormValues?.bearing_deg, setValue])

  // Notify parent of value changes
  useEffect(() => {
    if (onValuesChange) {
      onValuesChange(watchedValues)
    }
  }, [watchedValues, onValuesChange])

  // Get door constraints for current door type
  const doorConstraints = useMemo(() => {
    return DOOR_TYPE_DEFAULTS[watchedValues.default_door_type as DoorType]
  }, [watchedValues.default_door_type])

  // Handle unit count change
  const handleUnitCountChange = (value: number[]) => {
    setValue('unit_count', value[0])
    // Clear overrides when unit count changes
    setValue('unit_overrides', [])
  }

  // Handle suggested fix
  const handleApplyFix = (fix: SuggestedFix) => {
    switch (fix.type) {
      case 'expand_building':
        if (fix.newValue) {
          setValue('width_ft', Math.ceil(fix.newValue))
        }
        break
      case 'redistribute':
        // Clear overrides to redistribute equally
        setValue('unit_overrides', [])
        break
      case 'reduce_count':
        if (fix.newValue) {
          setValue('unit_count', fix.newValue)
          setValue('unit_overrides', [])
        }
        break
    }
  }

  // Toggle unit override panel
  const toggleUnit = (index: number) => {
    const newOpen = new Set(openUnits)
    if (newOpen.has(index)) {
      newOpen.delete(index)
    } else {
      newOpen.add(index)
    }
    setOpenUnits(newOpen)
  }

  // Add width override for unit
  const addWidthOverride = (index: number, width: number) => {
    const currentOverrides = watchedValues.unit_overrides || []
    const existingIndex = currentOverrides.findIndex(o => o.index === index)

    if (existingIndex >= 0) {
      // Update existing
      const updated = [...currentOverrides]
      updated[existingIndex] = { ...updated[existingIndex], width_ft: width }
      setValue('unit_overrides', updated)
    } else {
      // Add new
      setValue('unit_overrides', [...currentOverrides, { index, width_ft: width }])
    }
  }

  // Add type override for unit
  const addTypeOverride = (index: number, type: UnitType) => {
    const currentOverrides = watchedValues.unit_overrides || []
    const existingIndex = currentOverrides.findIndex(o => o.index === index)

    if (existingIndex >= 0) {
      const updated = [...currentOverrides]
      updated[existingIndex] = { ...updated[existingIndex], type }
      setValue('unit_overrides', updated)
    } else {
      setValue('unit_overrides', [...currentOverrides, { index, type }])
    }
  }

  // Remove override for unit
  const removeOverride = (index: number) => {
    const currentOverrides = watchedValues.unit_overrides || []
    setValue('unit_overrides', currentOverrides.filter(o => o.index !== index))
  }

  // Get override for unit
  const getOverride = (index: number) => {
    return watchedValues.unit_overrides?.find(o => o.index === index)
  }

  // Update door override for unit
  const updateUnitDoors = (index: number, doors: UnitDoorsConfig) => {
    const currentOverrides = watchedValues.unit_overrides || []
    const existingIndex = currentOverrides.findIndex(o => o.index === index)

    if (existingIndex >= 0) {
      // Update existing override
      const updated = [...currentOverrides]
      updated[existingIndex] = { ...updated[existingIndex], doors }
      setValue('unit_overrides', updated)
    } else {
      // Add new override
      setValue('unit_overrides', [...currentOverrides, { index, doors }])
    }
  }

  // Get effective door configuration for unit (defaults + overrides)
  const getEffectiveUnitDoors = (index: number): UnitDoorsConfig => {
    const unitWidth = layoutResult.widths[index] ?? 0
    return getUnitDoorsFromParams(watchedValues, index, unitWidth, watchedValues.unit_count)
  }

  // Copy one unit's doors to all other units
  const copyDoorsToAllUnits = (sourceIndex: number) => {
    const sourceDoors = getEffectiveUnitDoors(sourceIndex)
    const currentOverrides = watchedValues.unit_overrides || []
    const newOverrides = Array.from({ length: watchedValues.unit_count }, (_, i) => ({
      index: i,
      width_ft: currentOverrides.find(o => o.index === i)?.width_ft,
      doors: sourceDoors,
    }))
    setValue('unit_overrides', newOverrides)
  }

  // Calculate interior depth for door configuration
  const interiorDepth = watchedValues.depth_ft - 2 * watchedValues.wall_thickness_ft

  // Show position
  const latitude = previewLatitude?.toFixed(6) || watchedValues.latitude?.toFixed(6) || '—'
  const longitude = previewLongitude?.toFixed(6) || watchedValues.longitude?.toFixed(6) || '—'

  const onSubmit = async (values: SingleRowParams) => {
    // Validate pricing form
    const pricingValid = await pricingForm.trigger()
    if (!pricingValid) return

    // Include position from preview
    if (previewLatitude !== undefined && previewLongitude !== undefined) {
      values.latitude = previewLatitude
      values.longitude = previewLongitude
    }
    onSave(values, pricingForm.getValues())
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full flex-1 min-h-0">
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-4 space-y-6">
          {/* Building Section Header */}
          <div className="bg-blue-50 dark:bg-blue-950/30 -mx-4 px-4 py-2 border-y">
            <h3 className="font-semibold text-sm text-blue-700 dark:text-blue-300">Building Envelope</h3>
            <p className="text-xs text-muted-foreground">Overall structure dimensions</p>
          </div>

          {/* Identity Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Identity</h4>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input id="name" {...register('name')} placeholder="e.g., Box Hangar Row A" />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="code">Code</Label>
                <Input id="code" {...register('code')} placeholder="e.g., BH-A" />
                {errors.code && <p className="text-xs text-destructive">{errors.code.message}</p>}
              </div>
            </div>
          </div>

          <Separator />

          {/* Dimensions Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Dimensions (feet)</h4>

            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-2">
                <Label htmlFor="width_ft">Width</Label>
                <Input
                  id="width_ft"
                  type="number"
                  step="any"
                  {...register('width_ft', { valueAsNumber: true })}
                />
                {errors.width_ft && (
                  <p className="text-xs text-destructive">{errors.width_ft.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="depth_ft">Depth</Label>
                <Input
                  id="depth_ft"
                  type="number"
                  step="any"
                  {...register('depth_ft', { valueAsNumber: true })}
                />
                {errors.depth_ft && (
                  <p className="text-xs text-destructive">{errors.depth_ft.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="height_ft">Height</Label>
                <Input
                  id="height_ft"
                  type="number"
                  step="any"
                  {...register('height_ft', { valueAsNumber: true })}
                />
                {errors.height_ft && (
                  <p className="text-xs text-destructive">{errors.height_ft.message}</p>
                )}
              </div>
            </div>

            {/* Wall Thickness */}
            <div className="space-y-2">
              <Label htmlFor="wall_thickness_ft">Wall Thickness (ft)</Label>
              <Input
                id="wall_thickness_ft"
                type="number"
                step="any"
                min={0.25}
                max={2}
                {...register('wall_thickness_ft', { valueAsNumber: true })}
              />
              <p className="text-xs text-muted-foreground">
                0.25 - 2 ft (default: 0.5 ft / 6 inches)
              </p>
              {errors.wall_thickness_ft && (
                <p className="text-xs text-destructive">{errors.wall_thickness_ft.message}</p>
              )}
            </div>

          </div>

          <Separator />

          {/* Unit Configuration Section */}
          <div className="bg-green-50 dark:bg-green-950/30 -mx-4 px-4 py-2 border-y">
            <h3 className="font-semibold text-sm text-green-700 dark:text-green-300">Unit Configuration</h3>
            <p className="text-xs text-muted-foreground">Number and layout of units</p>
          </div>

          <div className="space-y-4">
            {/* Unit count slider */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Number of Units</Label>
                <span className="text-sm font-medium">{watchedValues.unit_count}</span>
              </div>
              <Slider
                value={[watchedValues.unit_count]}
                onValueChange={handleUnitCountChange}
                min={1}
                max={20}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>1</span>
                <span>20</span>
              </div>
            </div>

            {/* Layout Preview */}
            <div className="space-y-2">
              <Label>Unit Layout</Label>
              <UnitLayoutPreview
                unitWidths={layoutResult.widths}
                buildingWidth={watchedValues.width_ft}
                wallThickness={watchedValues.wall_thickness_ft}
              />
            </div>

            {/* Constraint Warning */}
            {!layoutResult.isBalanced && (
              <ConstraintWarning
                difference={layoutResult.difference_ft}
                buildingWidth={watchedValues.width_ft}
                suggestedFixes={layoutResult.suggestedFixes}
                onApplyFix={handleApplyFix}
              />
            )}

            {/* Unit width info */}
            <div className="text-xs text-muted-foreground space-y-1">
              <div className="flex justify-between">
                <span>Average unit width:</span>
                <span>{(layoutResult.totalUnitsWidth / watchedValues.unit_count).toFixed(1)} ft</span>
              </div>
              <div className="flex justify-between">
                <span>Total wall thickness:</span>
                <span>{layoutResult.totalWallsWidth.toFixed(1)} ft</span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Default Door Config Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Default Door Configuration</h4>
            <p className="text-xs text-muted-foreground">Applied to all units unless overridden</p>

            <div className="space-y-2">
              <Label htmlFor="default_door_type">Door Type</Label>
              <Select
                value={watchedValues.default_door_type}
                onValueChange={(value) => setValue('default_door_type', value as DoorType)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bifold">Bi-fold (30-100 ft)</SelectItem>
                  <SelectItem value="sliding">Sliding (40-200 ft)</SelectItem>
                  <SelectItem value="hydraulic">Hydraulic (50-150 ft)</SelectItem>
                  <SelectItem value="rolling">Rolling (20-60 ft)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="default_door_width_ft">Door Width (ft)</Label>
                <Input
                  id="default_door_width_ft"
                  type="number"
                  step="any"
                  min={doorConstraints.min_width_ft}
                  max={doorConstraints.max_width_ft}
                  {...register('default_door_width_ft', { valueAsNumber: true })}
                />
                <p className="text-xs text-muted-foreground">
                  {doorConstraints.min_width_ft}-{doorConstraints.max_width_ft} ft
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="default_door_height_ft">Door Height (ft)</Label>
                <Input
                  id="default_door_height_ft"
                  type="number"
                  step="any"
                  {...register('default_door_height_ft', { valueAsNumber: true })}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Individual Unit Adjustments */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Individual Unit Adjustments</h4>
            <p className="text-xs text-muted-foreground">Override width for specific units</p>

            <div className="space-y-2">
              {Array.from({ length: watchedValues.unit_count }, (_, i) => {
                const override = getOverride(i)
                const unitWidth = override?.width_ft ?? layoutResult.widths[i]
                const isOpen = openUnits.has(i)
                const hasOverride = override !== undefined

                return (
                  <Collapsible key={i} open={isOpen} onOpenChange={() => toggleUnit(i)}>
                    <CollapsibleTrigger asChild>
                      <button
                        type="button"
                        className={cn(
                          'w-full flex items-center justify-between p-2 rounded-md text-sm',
                          'hover:bg-muted/50 transition-colors',
                          hasOverride && 'bg-amber-50 dark:bg-amber-950/30'
                        )}
                      >
                        <div className="flex items-center gap-2">
                          {isOpen ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                          <span>Unit {i + 1}</span>
                          {hasOverride && (
                            <span className="text-xs text-amber-600 dark:text-amber-400">(custom)</span>
                          )}
                        </div>
                        <span className="text-muted-foreground">{unitWidth?.toFixed(1)} ft</span>
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="p-3 border rounded-md mt-1 space-y-4">
                        {/* Unit Type Override */}
                        <div className="space-y-2">
                          <Label>Unit Type</Label>
                          <Select
                            value={override?.type || 'box_hangar'}
                            onValueChange={(value) => addTypeOverride(i, value as UnitType)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ALLOWED_UNIT_TYPES_FOR_AREA.box_hangar_single_row.map((t) => (
                                <SelectItem key={t} value={t}>
                                  {UNIT_TYPE_LABELS[t]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Width Override */}
                        <div className="space-y-2">
                          <Label>Width Override (ft)</Label>
                          <div className="flex gap-2">
                            <Input
                              type="number"
                              step="any"
                              value={override?.width_ft ?? layoutResult.widths[i] ?? 0}
                              onChange={(e) => addWidthOverride(i, parseFloat(e.target.value))}
                              min={15}
                              className="flex-1"
                            />
                            {hasOverride && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeOverride(i)}
                                title="Remove override"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </div>

                        <Separator />

                        {/* Door Configuration */}
                        <SingleRowUnitDoorEditor
                          unitIndex={i}
                          unitCount={watchedValues.unit_count}
                          doors={getEffectiveUnitDoors(i)}
                          onChange={(doors) => updateUnitDoors(i, doors)}
                          unitWidthFt={unitWidth ?? 0}
                          unitDepthFt={interiorDepth}
                          unitHeightFt={watchedValues.height_ft}
                          onCopyToAll={() => copyDoorsToAllUnits(i)}
                        />
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )
              })}
            </div>
          </div>

          <Separator />

          {/* Appearance Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Appearance</h4>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="floor_color">Floor Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="floor_color"
                    type="color"
                    {...register('floor_color')}
                    className="w-12 h-9 p-1 cursor-pointer"
                  />
                  <Input {...register('floor_color')} className="flex-1" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="wall_color">Wall Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="wall_color"
                    type="color"
                    {...register('wall_color')}
                    className="w-12 h-9 p-1 cursor-pointer"
                  />
                  <Input {...register('wall_color')} className="flex-1" />
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Position Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Position</h4>
            <p className="text-xs text-muted-foreground">Drag the building on the map to set position</p>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Latitude</Label>
                <div className="px-3 py-2 bg-muted rounded-md text-sm font-mono">{latitude}</div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Longitude</Label>
                <div className="px-3 py-2 bg-muted rounded-md text-sm font-mono">{longitude}</div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bearing_deg">Rotation (degrees)</Label>
              <div className="flex gap-2 items-center">
                <Input
                  id="bearing_deg"
                  type="number"
                  step="any"
                  min={0}
                  max={360}
                  {...register('bearing_deg', { valueAsNumber: true })}
                  className="flex-1"
                />
                <span className="text-sm text-muted-foreground">°</span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Pricing & Building Features */}
          <Form {...pricingForm}>
            <PricingFeaturesSection form={pricingForm} />
          </Form>

          {/* Remove Area - only in edit mode */}
          {!isCreating && areaId && onRemoved && (
            <RemoveAreaButton
              areaId={areaId}
              areaName={areaName || 'this area'}
              onRemoved={onRemoved}
            />
          )}
        </div>
      </ScrollArea>

      {/* Sticky footer with actions */}
      <div className="border-t p-4 flex gap-2 shrink-0">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1" disabled={isSaving}>
          Cancel
        </Button>
        <Button type="submit" className="flex-1" disabled={!layoutResult.isBalanced || isSaving || !isPlaced}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            isCreating ? 'Create Building' : 'Save Changes'
          )}
        </Button>
      </div>
    </form>
  )
}
